/**
 * Login URL Resolution
 *
 * Resolves the correct login URL for a user based on their organisation
 * membership. Global admins get `/admin/login`; organisation users get
 * `/admin/login/<root-parent-org-slug>` — child organisations are always
 * resolved up to their root parent so the branded login surface is
 * single-tenanted at the corporate level.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

import { buildTenantUrl } from '@/lib/host/tenantUrl';
import { isPhoneShadowEmail } from '@/lib/referral/phoneShadowEmail';

// Defensive cap on the parent-walk depth — protects against accidental
// cycles in organisations.parent_org_id whilst comfortably accommodating
// the deepest legitimate hierarchy we support.
const MAX_PARENT_WALK_DEPTH = 8;

/**
 * Walks the `parent_org_id` chain starting at `organizationId` and returns
 * the slug of the topmost (root) organisation. Returns `null` if no row is
 * found at any level or if the depth cap is reached.
 *
 * Branded login pages live only at the root level; staff of a child org
 * must sign in via their parent's branded URL.
 */
export async function resolveRootOrgSlug(
  supabaseAdmin: SupabaseClient,
  organizationId: string,
): Promise<string | null> {
  let currentId: string = organizationId;

  for (let depth = 0; depth < MAX_PARENT_WALK_DEPTH; depth += 1) {
    const { data } = await supabaseAdmin
      .from('organizations')
      .select('slug, parent_org_id')
      .eq('id', currentId)
      .single<{ slug: string | null; parent_org_id: string | null }>();

    if (!data?.slug) return null;

    if (!data.parent_org_id) return data.slug;

    currentId = data.parent_org_id;
  }

  return null;
}

/**
 * Resolves the branded login URL for an organisation by its ID. The slug
 * returned is always the **root parent's** slug — child orgs never get
 * their own branded login page. Falls back to the global login URL if
 * the chain cannot be resolved.
 */
export async function resolveLoginUrlByOrgId(
  supabaseAdmin: SupabaseClient,
  appUrl: string,
  organizationId: string,
): Promise<string> {
  const rootSlug = await resolveRootOrgSlug(supabaseAdmin, organizationId);
  if (rootSlug) {
    // `buildTenantUrl` returns the subdomain form
    // (`https://<slug>.<apex>/admin/login`) when `appUrl` resolves to a
    // recognised apex; otherwise the historical path form
    // (`${appUrl}/admin/login/<slug>`) — used for localhost, previews
    // and any host that fails the recognised-apex check.
    return buildTenantUrl({ appUrl, slug: rootSlug, path: '/admin/login' });
  }
  return `${appUrl}/admin/login`;
}

/**
 * Returns the customer-facing slug for an organisation — the **child** org's
 * own slug if `parent_org_id IS NOT NULL`, otherwise `null`. Mirrors the
 * resolution rule of {@link resolveCustomerLoginUrlByOrgId} but yields the
 * raw slug so callers (e.g. the branded magic-link interstitial) can build
 * their own URLs without re-implementing the lookup.
 *
 *   - child org → that org's slug
 *   - root parent org → `null` (customers should never be invited into a parent)
 *   - lookup failure → `null`
 */
export async function resolveCustomerOrgSlug(
  supabaseAdmin: SupabaseClient,
  organizationId: string,
): Promise<string | null> {
  const { data } = await supabaseAdmin
    .from('organizations')
    .select('slug, parent_org_id')
    .eq('id', organizationId)
    .single<{ slug: string | null; parent_org_id: string | null }>();

  if (data?.slug && data.parent_org_id) return data.slug;
  return null;
}

/**
 * Customer counterpart to {@link resolveLoginUrlByOrgId}. Returns the branded
 * passwordless-customer login URL for the organisation.
 *
 * Inverse of the staff rule: customer branded login is single-tenanted at the
 * **child** org level — customers are invited into specific child orgs, so the
 * returned slug is always the org's own slug (never walked up to the root
 * parent). See `app/customer/login/[org-slug]/page.tsx`, which redirects
 * parent slugs to the slug-less notice page; emitting a parent slug here
 * would break the branded form for the customer.
 *
 * Resolution:
 *   - child org (`parent_org_id IS NOT NULL`) → `${appUrl}/customer/login/${slug}`
 *   - root parent org (`parent_org_id IS NULL`) → slug-less `${appUrl}/customer/login`
 *     (defensive: customers should never be invited into a parent org)
 *   - lookup failure → slug-less `${appUrl}/customer/login`
 */
export async function resolveCustomerLoginUrlByOrgId(
  supabaseAdmin: SupabaseClient,
  appUrl: string,
  organizationId: string,
): Promise<string> {
  const slug = await resolveCustomerOrgSlug(supabaseAdmin, organizationId);
  if (slug) {
    return buildTenantUrl({ appUrl, slug, path: '/customer/login' });
  }
  return `${appUrl}/customer/login`;
}

/**
 * Appends `?username=…` to a customer login URL so the branded login form
 * can prefill the identifier (issue #779). Invitations to an *existing*
 * customer point at the login page rather than the `/r` referral link,
 * which already prefills from the invitation itself, leaving the customer
 * to guess which identifier they registered with.
 *
 * Returns the URL unchanged when there is nothing usable to carry — an
 * empty username, or the internal `sms-…@customer-login.invalid` shadow
 * email, which is never a valid sign-in identifier.
 */
export function withLoginUsername(
  loginUrl: string,
  username?: string | null,
): string {
  const trimmed = typeof username === 'string' ? username.trim() : '';
  if (!trimmed || isPhoneShadowEmail(trimmed)) return loginUrl;
  const separator = loginUrl.includes('?') ? '&' : '?';
  return `${loginUrl}${separator}username=${encodeURIComponent(trimmed)}`;
}

/**
 * Resolves the login URL for a user by looking up their profile and
 * organisation membership. Global admins get the global login URL;
 * org users get their branded login URL.
 *
 * @param email – the user's email address
 */
export async function resolveLoginUrlByEmail(
  supabaseAdmin: SupabaseClient,
  appUrl: string,
  email: string,
): Promise<string> {
  const globalUrl = `${appUrl}/admin/login`;

  // Resolve the user in auth.users via the `find_auth_user_by_email` RPC. The
  // previous `auth.admin.listUsers()` lookup only returned the first GoTrue
  // page (default 50 users) and matched case-sensitively, so on any project
  // with more than one page — or a differently-cased email — the user was
  // silently missed and every caller fell back to the global login URL. The
  // SECURITY DEFINER helper does an exact, case-insensitive match against
  // auth.users regardless of project size.
  const { data: authUserRows } = await supabaseAdmin.rpc(
    'find_auth_user_by_email',
    { p_email: email },
  );
  const user = (
    Array.isArray(authUserRows) ? (authUserRows[0] ?? null) : authUserRows
  ) as { id: string } | null;
  if (!user) return globalUrl;

  // Platform-admin check — replaces legacy `user_type === 'global_admin'`
  // (Phase 6 P3 — RBAC v2 migration). Inlined rather than going via
  // `userHasPermissionAnywhere` so we use the caller-supplied client
  // (this helper is dependency-injected; the apiAuth helper is singleton-backed).
  const { data: isPlatformAdmin } = await supabaseAdmin.rpc(
    'user_has_permission_anywhere',
    { user_uuid: user.id, perm_slug: 'users.manage_global_admins' },
  );
  if (isPlatformAdmin === true) return globalUrl;

  // Look up the user's org
  const { data: assignment } = await supabaseAdmin
    .from('user_role_assignments')
    .select('organization_id')
    .eq('user_id', user.id)
    .limit(1)
    .single();

  if (!assignment?.organization_id) return globalUrl;

  return resolveLoginUrlByOrgId(supabaseAdmin, appUrl, assignment.organization_id);
}
